import { PERIOD_TIME_UNIT_ENUM } from '../../../../../enums/PeriodTimeUnitEnum';
import { DATE_TIME_FORMAT_NAME_MODEL, getDateFormat } from '../../../../../Apis/functions';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle: Full Stack Developer - IBL
 * @Email: clopez@ibl.mx
 *
 * Utility to calculate date/time ranges based on a frequency (days, weeks, months, years).
 * Exposes helpers to get the range in milliseconds and also formatted for UI.
 */
const FrequencyRange = () => {

    /**
     * Returns the provided date adjusted to the start of the day (00:00:00.000).
     */
    const startOfDay = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    /**
     * Returns the provided date adjusted to the end of the day (23:59:59.999).
     */
    const endOfDay = (date) => {
        const d = new Date(date);
        d.setHours(23, 59, 59, 999);
        return d;
    };

    /**
     * Adds a number of days to a date and returns a new instance.
     */
    const addDays = (date, days) => {
        const d = new Date(date);
        d.setDate(d.getDate() + Number(days));
        return d;
    };

    /**
     * Adds weeks to a date (weeks * 7 days).
     */
    const addWeeks = (date, weeks) => addDays(date, Number(weeks) * 7);

    /**
     * Safely adds months to a date to avoid overflow issues.
     * Adjusts the day to the last day of the target month if the original day does not exist.
     */
    const addMonthsSafe = (date, months) => {
        const d = new Date(date);
        const day = d.getDate();

        d.setDate(1);
        d.setMonth(d.getMonth() + Number(months));

        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(day, lastDay));

        return d;
    };

    /**
     * Adds years to a date by reusing the safe month addition (years * 12).
     */
    const addYearsSafe = (date, years) => addMonthsSafe(date, Number(years) * 12);

    /**
     * Calculates the frequency range (start and end) from a base date.
     * - Normalizes the start to the beginning of the day.
     * - Computes the end based on the unit (days, weeks, months, years).
     * - Adjusts the end to cover the last second of the period.
     */
    const getFrequencyRange = (frequencyValue, frequencyTimeUnit, baseDate = new Date()) => {
        const n = Number(frequencyValue);

        if (!Number.isFinite(n) || n <= 0 || !frequencyTimeUnit) {
            return { frequencyStart: null, frequencyEnd: null };
        }

        const start = startOfDay(baseDate);

        let periodDate;
        switch (frequencyTimeUnit) {
            case PERIOD_TIME_UNIT_ENUM.DAYS.name:
                periodDate = addDays(start, n);
                break;
            case PERIOD_TIME_UNIT_ENUM.WEEKS.name:
                periodDate = addWeeks(start, n);
                break;
            case PERIOD_TIME_UNIT_ENUM.MONTHS.name:
                periodDate = addMonthsSafe(start, n);
                break;
            case PERIOD_TIME_UNIT_ENUM.YEARS.name:
                periodDate = addYearsSafe(start, n);
                break;
            default:
                return { frequencyStart: null, frequencyEnd: null };
        }

        const end = new Date(periodDate);
        end.setSeconds(end.getSeconds() - 1);
        const frequencyEnd = endOfDay(end);

        return {
            frequencyStart: start.getTime(),
            frequencyEnd: frequencyEnd.getTime(),
        };
    };

    /**
     * Builds a UI-ready range object:
     * - Calculates the range in milliseconds using `getFrequencyRange`.
     * - Returns formatted start/end plus the raw ms values.
     */
    const buildFrequencyRange = (value, unit, format = DATE_TIME_FORMAT_NAME_MODEL.shortMonth) => {
        const fmtDate = (ms) => getDateFormat(ms, false, format);
        const { frequencyStart, frequencyEnd } = getFrequencyRange(value, unit);
        if (!frequencyStart || !frequencyEnd) return null;

        return {
            start: fmtDate(frequencyStart),
            end: fmtDate(frequencyEnd),
            startMs: frequencyStart,
            endMs: frequencyEnd,
        };
    };


    return {
        getFrequencyRange,
        buildFrequencyRange
    };

};

export default FrequencyRange;
